import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexChart,
  ApexLegend,
  ApexTooltip,
  ApexStroke,
  ApexResponsive,
  NgApexchartsModule,
  ChartComponent,
} from "ng-apexcharts";
import { CommonModule } from "@angular/common";
import { ProviderClaimTypesGetDTO } from "src/app/pages/providers/provider.model";
import { TypeSumary } from "../../models/unclaim-cases-ui.model";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DashboardChartEmptyStateComponent } from "../../shared/dashboard-chart-empty-state/dashboard-chart-empty-state.component";

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  plotOptions: ApexPlotOptions;
  legend: ApexLegend;
  colors: string[];
  responsive: ApexResponsive[];
  tooltip?: ApexTooltip;
  stroke?: ApexStroke;
  dataLabels?: any;
};

@Component({
  selector: "app-other-claim-chart",
  standalone: true,
  imports: [NgApexchartsModule, TranslateModule, CommonModule, MatTooltipModule, DashboardChartEmptyStateComponent],
  templateUrl: "./other-claim-chart.component.html",
  styleUrls: ["./other-claim-chart.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class OtherClaimChartComponent {
  @Input({ required: true }) typeSummary: TypeSumary = {};
  @Input({ required: true }) typesApi: ProviderClaimTypesGetDTO[] = [];
  @Output() readonly claimTypeSelected = new EventEmitter<string>();
  @ViewChild("chart") chart: ChartComponent;
  private readonly translate = inject(TranslateService);

  legendItems: {
    label: string;
    color: string;
    claimTypeValue: string;
    amount: number;
    total: number;
  }[] = [];

  readonly legendLabelMaxLength = 40;

  get hasData(): boolean {
    return Object.keys(this.typeSummary || {}).length > 0;
  }
  public chartOptions: Partial<ChartOptions>;
  private mutationObserver: MutationObserver;
  dr = inject(ChangeDetectorRef);

  constructor() {
    this.chartOptions = {
      series: [],
      chart: {
        type: "donut",
        width: 150,
        height: 200,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "85%",

            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "9px",
                fontWeight: "600",
                color: "#777",
                offsetY: -8,
                formatter: () => this.translate.instant("dashboard.claims"),
              },
              value: {
                show: true,
                fontSize: "14px",
                fontWeight: "700",
                color: "#777",
                offsetY: 4,
                formatter: (val: any) => {
                  const typeKeys = Object.keys(this.typeSummary);
                  const sumTotal = typeKeys.reduce(
                    (acc, key) => acc + (this.typeSummary[key].total || 0),
                    0
                  );
                  return Math.round(sumTotal).toString();
                },
              },
              total: {
                show: true,
                formatter: (val: any) => {
                  const typeKeys = Object.keys(this.typeSummary);
                  const sumTotal = typeKeys.reduce(
                    (acc, key) => acc + (this.typeSummary[key].total || 0),
                    0
                  );
                  return Math.round(sumTotal).toString();
                },
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
        style: {
          colors: ["#2a3547"],
          fontWeight: "bold",
          fontSize: "12px",
        },
      },
      colors: [],
      labels: [],
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
        lineCap: "round",
      },
      legend: {
        show: false,
        position: "right",
        floating: false,
        fontSize: "12px",
        itemMargin: {
          vertical: 8,
        },
        labels: {
          colors: "#2a3547",
        },
      },
      responsive: [
        {
          breakpoint: 550,
          options: {
            chart: {
              height: 168,
              width: 120,
              type: "donut",
            },
            legend: {
              show: false,
            },
            plotOptions: {
              pie: {
                donut: {
                  size: "85%",
                  labels: {
                    show: true,
                    name: {
                      show: true,
                      fontSize: "9px",
                      fontWeight: "600",
                      color: "#777",
                      offsetY: -8,
                      formatter: () =>
                        this.translate.instant("dashboard.claims"),
                    },
                    value: {
                      show: true,
                      fontSize: "14px",
                      fontWeight: "700",
                      color: this.isDarkMode() ? "#fdfdfdff" : "#2a3547",
                      offsetY: 4,
                      formatter: (val: any) => {
                        const typeKeys = Object.keys(this.typeSummary);
                        const sumTotal = typeKeys.reduce(
                          (acc, key) =>
                            acc + (this.typeSummary[key].total || 0),
                          0
                        );
                        return Math.round(sumTotal).toString();
                      },
                    },
                    total: {
                      show: false,
                    },
                  },
                },
              },
            },
          },
        },
      ],
    };
  }

  ngOnInit(): void {
    this.updateColorsFromTheme();

    this.mutationObserver = new MutationObserver(() => {
      this.updateColorsFromTheme();
      this.updateSeries();
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.updateSeries();

    this.translate.onLangChange.subscribe(() => {
      this.chartOptions = {
        series: [],
        chart: {
          height: 170,
          width: "100%",
          type: "donut",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "85%",

              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "9px",
                  fontWeight: "600",
                  color: "#777",
                  offsetY: -8,
                  formatter: () => this.translate.instant("dashboard.claims"),
                },
                value: {
                  show: true,
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#777",
                  offsetY: 4,
                  formatter: (val: any) => {
                    const typeKeys = Object.keys(this.typeSummary);
                    const sumTotal = typeKeys.reduce(
                      (acc, key) => acc + (this.typeSummary[key].total || 0),
                      0
                    );
                    return Math.round(sumTotal).toString();
                  },
                },
                total: {
                  show: true,
                  formatter: (val: any) => {
                    const typeKeys = Object.keys(this.typeSummary);
                    const sumTotal = typeKeys.reduce(
                      (acc, key) => acc + (this.typeSummary[key].total || 0),
                      0
                    );
                    return Math.round(sumTotal).toString();
                  },
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
          style: {
            colors: ["#2a3547"],
            fontWeight: "bold",
            fontSize: "12px",
          },
        },
        colors: [],
        labels: [],
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
          lineCap: "round",
        },
        legend: {
          show: false,
          position: "right",
          floating: false,
          fontSize: "12px",
          itemMargin: {
            vertical: 8,
          },
          labels: {
            colors: "#2a3547",
          },
        },
        responsive: [
          {
            breakpoint: 550,
            options: {
              chart: {
                height: 168,
                width: 120,
                type: "donut",
              },
              legend: {
                show: false,
              },
              plotOptions: {
                pie: {
                  donut: {
                    size: "85%",
                    labels: {
                      show: true,
                      name: {
                        show: true,
                        fontSize: "9px",
                        fontWeight: "600",
                        color: "#777",
                        offsetY: -8,
                        formatter: () =>
                          this.translate.instant("dashboard.claims"),
                      },
                      value: {
                        show: true,
                        fontSize: "14px",
                        fontWeight: "700",
                        color: this.isDarkMode() ? "#fdfdfdff" : "#2a3547",
                        offsetY: 4,
                        formatter: (val: any) => {
                          const typeKeys = Object.keys(this.typeSummary);
                          const sumTotal = typeKeys.reduce(
                            (acc, key) =>
                              acc + (this.typeSummary[key].total || 0),
                            0
                          );
                          return Math.round(sumTotal).toString();
                        },
                      },
                      total: {
                        show: false,
                      },
                    },
                  },
                },
              },
            },
          },
        ],
      };
      this.updateSeries();
      //this.updateValueLabelColor();
      this.updateColorsFromTheme();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["typeSummary"] || changes["typesApi"]) {
      this.updateSeries();

      this.updateColorsFromTheme();
    }
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private isDarkMode(): boolean {
    return document.documentElement.classList.contains("dark-theme");
  }

  // private updateValueLabelColor(): void {
  //   const valueColor = this.isDarkMode() ? "#fafafaff" : "#2a3547";

  //   // Update main donut labels
  //   if (this.chartOptions.plotOptions?.pie?.donut?.labels?.value) {
  //     this.chartOptions.plotOptions.pie.donut.labels.value.color = valueColor;
  //   }

  //   // Update responsive labels
  //   if (
  //     this.chartOptions.responsive?.[0]?.options?.plotOptions?.pie?.donut
  //       ?.labels?.value
  //   ) {
  //     this.chartOptions.responsive[0].options.plotOptions.pie.donut.labels.value.color =
  //       valueColor;
  //   }
  // }

  private updateSeries(): void {
    const typeKeys = Object.keys(this.typeSummary);
    const newSeries = typeKeys.map((key) => this.typeSummary[key].total ?? 0);
    const newLabels = typeKeys.map((key) => this.getClaimTypeLabel(key));

    const textColor = this.isDarkMode() ? "#FFFFFF" : "#2a3547";

    this.chartOptions.series = newSeries;
    this.chartOptions.labels = newLabels;
    this.chartOptions.plotOptions!.pie!.donut!.labels!.total!.color = textColor;

    this.legendItems = typeKeys.map((key, index) => ({
      label: this.getClaimTypeLabel(key),
      color:
        this.chartOptions.colors?.[index % (this.chartOptions.colors?.length || 1)] ??
        "#ccc",
      claimTypeValue: key,
      amount: this.typeSummary[key]?.amount ?? 0,
      total: this.typeSummary[key]?.total ?? 0,
    }));

    // Set up custom tooltip
    this.chartOptions.tooltip = {
      enabled: true,
      custom: ({ seriesIndex, w }: any) => {
        const typeKeys = Object.keys(this.typeSummary || {});
        const key = typeKeys[seriesIndex] ?? "";
        const type = this.typeSummary[key];
        if (!type) return "";

        const label = newLabels?.[seriesIndex] ?? key ?? "";
        const claimTotal = type.total ?? 0;
        const amount = type.amount ?? 0;

        const amountFormatted = new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }).format(amount);

        const color = w.config.colors?.[seriesIndex] || "#1972c5";

        // Get translated labels
        const totalLabel = this.translate.instant("dashboard.tooltip.total");
        const amountLabel = this.translate.instant("dashboard.tooltip.amount");

        return (
          `<div style="padding:10px;font-size:12px;background:${color};border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,0.15)">` +
          `<div style="color:#fff;font-weight:700;margin-bottom:6px">${label}</div>` +
          `<div style="color:#fff;margin-bottom:4px"><span>${totalLabel}:</span> <strong>${claimTotal}</strong></div>` +
          `<div style="color:#fff"><span>${amountLabel}:</span> <strong>${amountFormatted}</strong></div>` +
          `</div>`
        );
      },
    };

    this.chartOptions.legend = {
      ...this.chartOptions.legend,
      labels: {
        colors: textColor,
      },
    };
  }

  private updateColorsFromTheme(): void {
    const root = getComputedStyle(document.documentElement);

    const baseColors: string[] = [];
    const maxColors = 40;

    for (let i = 1; i <= maxColors; i++) {
      const varName = `--mat-charts-color-${this.numberToWord(i)}`;
      const color = root.getPropertyValue(varName).trim();
      if (color) {
        baseColors.push(color);
      }
    }

    const typeCount = Object.keys(this.typeSummary).length;
    const colors = Array.from(
      { length: typeCount },
      (_, i) => baseColors[i % baseColors.length]
    );

    this.chartOptions.colors = colors;

    const isDark = this.isDarkMode();
    const centerTextColor = isDark ? "#fff" : "#2a3547";

    // Initialize properties when missing
    if (!this.chartOptions.plotOptions) {
      this.chartOptions.plotOptions = {
        pie: { donut: { size: "85%", labels: {} } },
      };
    }

    if (!this.chartOptions.plotOptions.pie) {
      this.chartOptions.plotOptions.pie = {
        donut: { size: "85%", labels: {} },
      };
    }

    if (!this.chartOptions.plotOptions.pie.donut) {
      this.chartOptions.plotOptions.pie.donut = { size: "85%", labels: {} };
    }

    if (!this.chartOptions.plotOptions.pie.donut.labels) {
      this.chartOptions.plotOptions.pie.donut.labels = {};
    }

    // Apply colors to center label text
    this.chartOptions.plotOptions.pie.donut.labels.name = {
      ...(this.chartOptions.plotOptions.pie.donut.labels.name || {}),
      fontSize: "9px",
      fontWeight: "600",
      offsetY: -8,
      color: centerTextColor,
    };

    this.chartOptions.plotOptions.pie.donut.labels.value = {
      ...(this.chartOptions.plotOptions.pie.donut.labels.value || {}),
      fontSize: "14px",
      fontWeight: "700",
      offsetY: 4,
      color: centerTextColor,
    };

    this.chartOptions.plotOptions.pie.donut.labels.total = {
      ...(this.chartOptions.plotOptions.pie.donut.labels.total || {}),
      color: centerTextColor,
    };

    this.chartOptions.legend = {
      show: false,
      position: "right",
      labels: {
        colors: isDark ? "#fff" : "#2a3547",
      },
      itemMargin: {
        horizontal: 0,
        vertical: 5,
      },
      formatter: function (val, opts) {
        return val;
      },
    };

    if (this.chart?.updateOptions) {
      this.chart.updateOptions({
        colors: this.chartOptions.colors,
        legend: this.chartOptions.legend,
        plotOptions: this.chartOptions.plotOptions,
      });
    }

    this.legendItems = this.legendItems.map((item, index) => ({
      ...item,
      color:
        this.chartOptions.colors?.[index % (this.chartOptions.colors?.length || 1)] ??
        item.color,
    }));
  }

  onLegendItemClick(claimTypeValue: string): void {
    this.claimTypeSelected.emit(claimTypeValue);
  }

  truncateLegendLabel(label: string): string {
    if (label.length <= this.legendLabelMaxLength) {
      return label;
    }

    return `${label.slice(0, this.legendLabelMaxLength - 1)}…`;
  }

  getLegendItemTooltip(label: string): string {
    return this.translate.instant("dashboard.view_claims_with_type", {
      claimType: label,
    });
  }

  private getClaimTypeLabel(claimTypeValue: string): string {
    const translationKey = `claimsTypes.${claimTypeValue}`;
    const translated = this.translate.instant(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    const match = this.typesApi.find((type) => type.value === claimTypeValue);
    return match?.name ?? claimTypeValue;
  }

  private numberToWord(n: number): string {
    const words = [
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
      "twenty",
      "twentyone",
      "twentytwo",
      "twentythree",
      "twentyfour",
      "twentyfive",
      "twentysix",
      "twentyseven",
      "twentyeight",
      "twentynine",
      "thirty",
      "thirtyone",
      "thirtytwo",
      "thirtythree",
      "thirtyfour",
      "thirtyfive",
      "thirtysix",
      "thirtyseven",
      "thirtyeight",
      "thirtynine",
      "forty",
    ];
    return words[n - 1] || "one";
  }
}
